// Textarea — 1:1 port of legacy `src/ui/Primitives.jsx:84–102`.
import { forwardRef, useState, type CSSProperties, type KeyboardEventHandler } from 'react';
import { useFormRowControl } from './FormRow.js';

export interface TextareaProps {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  rows?: number;
  style?: CSSProperties;
  readOnly?: boolean;
  ariaLabel?: string;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  // WHY (#3039): the control owns an internal focus flag for its ring, and it
  //   used to be the ONLY blur handler — a form library's `field.onBlur`
  //   (touched/validation bookkeeping) had nowhere to go. Both now run.
  onBlur?: () => void;
  // WHY (#3039): caller-owned description ids, merged with the FormRow's
  //   hint/error ids rather than replaced by them.
  ariaDescribedBy?: string | undefined;
  /**
   * Paints the error border AND sets `aria-invalid`. Omitted, only
   * `aria-invalid` follows the enclosing FormRow's error — the border does
   * not, so existing consumers keep their current look.
   */
  invalid?: boolean | undefined;
}

/**
 * @aiPurpose multi-line control that pairs with {@link FormRow} for its
 *   accessible name, description and validity state.
 * @aiInvariant the ref is forwarded to the native `<textarea>` so a form
 *   library can focus the field on an invalid submit.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    value,
    onChange,
    placeholder,
    rows = 3,
    style,
    readOnly,
    ariaLabel,
    onKeyDown,
    onBlur,
    ariaDescribedBy,
    invalid,
  },
  ref,
) {
  const [focus, setFocus] = useState(false);
  const field = useFormRowControl({ describedBy: ariaDescribedBy, invalid });
  return (
    <textarea
      ref={ref}
      id={field.id}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      aria-label={ariaLabel}
      aria-describedby={field.describedBy}
      aria-invalid={field.invalid ? true : undefined}
      onKeyDown={onKeyDown}
      onFocus={() => setFocus(true)}
      onBlur={() => {
        setFocus(false);
        onBlur?.();
      }}
      style={{
        width: '100%',
        padding: '8px 10px',
        borderRadius: 'var(--r-2)',
        // WHY: only the explicit `invalid` prop paints the error border, so
        // existing consumers that pass an error to FormRow alone keep their
        // current look; `aria-invalid` still follows the row.
        border: `1px solid ${
          invalid ? 'var(--status-error-fg)' : focus ? 'var(--accent-1)' : 'var(--border-1)'
        }`,
        boxShadow: focus && !invalid ? '0 0 0 3px var(--accent-ring)' : 'none',
        background: 'var(--bg-1)',
        color: 'var(--fg-1)',
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
        lineHeight: 1.5,
        resize: 'vertical',
        transition:
          'border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)',
        ...style,
      }}
    />
  );
});
